import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, Transition as _Transition, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-6066fb10"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "explore-time-page" }
const _hoisted_2 = { class: "page-title" }
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h1", null, "Explore dates", -1))
const _hoisted_4 = { class: "page-content" }
const _hoisted_5 = { class: "month-nav" }
const _hoisted_6 = { class: "month-label-container" }
const _hoisted_7 = { class: "day-picker" }
const _hoisted_8 = { class: "day-strip-viewport" }
const _hoisted_9 = ["onClick"]
const _hoisted_10 = {
  key: 0,
  class: "today-label"
}
const _hoisted_11 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "selection-indicator" }, null, -1))

import { computed, ref, watch } from 'vue'
import { DateTime } from 'luxon'
import CalendarIcon from '@/explore/time/CalendarIcon.vue'
import ScopeLoadProgress from '@/explore/ScopeLoadProgress.vue'
import TodayList from '@/explore/time/TodayList.vue'
import { ChevronLeftIcon, ChevronRightIcon } from '@zhuowenli/vue-feather-icons'
import { useExploreStore } from '@/explore/ExploreStore'
import { useViewScopeStore } from '../ViewScopeStore'
import { useAncestorsToday } from './AncestorsToday'
import { TodayItem } from './TodayItem'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { LoadMode } from '@/util/AsyncData'

const LEAP_YEAR = 2024
const BOX_STEP_REM = 3 // $box-size (2.5) + $box-gap (0.5)

export default /*#__PURE__*/_defineComponent({
  __name: 'ExploreTimePage',
  setup(__props) {

const exploreStore = useExploreStore()
const viewScopeStore = useViewScopeStore()
const ancestorsToday = useAncestorsToday()
const viewPersonStore = useViewPersonStore()

// Use leap year 2024 as base so Feb 29 is valid
const selectedDay = ref(DateTime.utc(LEAP_YEAR, DateTime.local().month, DateTime.local().day))

// Sliding strip state
const BASE_OFFSET = -3 * BOX_STEP_REM // -9rem, centers indices 3-9 in viewport
const stripOffset = ref(0)
const animating = ref(false)
let pendingDay: DateTime | null = null

// All events, keyed by "M-D" string
const allEventsMap = ref<Map<string, TodayItem[]>>(new Map())

const scopeLoading = computed(() => !viewScopeStore.getAsyncScopeInfo(exploreStore.startPersonId, LoadMode.EnsureLoaded)?.isLoaded)
const scopeInfoRef = computed(() => viewScopeStore.getAsyncScopeInfo(exploreStore.startPersonId, LoadMode.EnsureLoaded)?.data)

watch(scopeInfoRef, () => {
    if (scopeInfoRef.value) {
        const events = ancestorsToday.getAllAncestorEvents()

        // Add relation paths
        for (const item of events) {
            if (item.personId) {
                item.relationPath = viewPersonStore.getLoadedRelationPath(scopeInfoRef.value.startPersonId, item.personId)
            }
        }

        // Group by month-day
        const map = new Map<string, TodayItem[]>()
        for (const item of events) {
            const key = `${item.date.month}-${item.date.day}`
            if (!map.has(key)) map.set(key, [])
            map.get(key)!.push(item)
        }
        allEventsMap.value = map
    }
}, { immediate: true })

function dayKey(day: DateTime) {
    return `${day.month}-${day.day}`
}

const displayMonthText = ref(selectedDay.value.toFormat('MMMM'))
const monthDirection = ref('')

// 13 days centered on selectedDay (6 buffer on each side for multi-step clicks)
const extendedDays = computed(() => {
    const days: DateTime[] = []
    for (let offset = -6; offset <= 6; offset++) {
        days.push(wrapDay(selectedDay.value.plus({ days: offset })))
    }
    return days
})

const stripStyle = computed(() => ({
    transform: `translateX(${BASE_OFFSET + stripOffset.value}rem)`,
    transition: animating.value ? 'transform 0.25s ease' : 'none'
}))

// Wrap a date to stay within the leap year
function wrapDay(dt: DateTime) {
    if (dt.year > LEAP_YEAR) {
        return DateTime.utc(LEAP_YEAR, 1, 1).plus({ days: dt.diff(DateTime.utc(LEAP_YEAR + 1, 1, 1), 'days').days })
    }
    if (dt.year < LEAP_YEAR) {
        return DateTime.utc(LEAP_YEAR, 12, 31).minus({ days: DateTime.utc(LEAP_YEAR, 1, 1).diff(dt, 'days').days - 1 })
    }
    return dt
}

function isToday(day: DateTime) {
    const now = DateTime.local()
    return day.month === now.month && day.day === now.day
}

function hasEvents(day: DateTime) {
    return (allEventsMap.value.get(dayKey(day))?.length ?? 0) > 0
}

function navigateTo(day: DateTime) {
    if (animating.value) return
    const index = extendedDays.value.findIndex(d => d.month === day.month && d.day === day.day)
    if (index === -1) return
    const offset = index - 6 // distance from center
    if (offset === 0) return

    // Animate month label if crossing a month boundary
    const targetMonth = day.toFormat('MMMM')
    if (targetMonth !== displayMonthText.value) {
        monthDirection.value = offset > 0 ? 'month-left' : 'month-right'
        displayMonthText.value = targetMonth
    }

    pendingDay = day
    animating.value = true
    stripOffset.value = -offset * BOX_STEP_REM
}

function onSlideEnd() {
    if (!pendingDay) return
    animating.value = false
    stripOffset.value = 0
    selectedDay.value = pendingDay
    pendingDay = null
}

function selectDay(day: DateTime) {
    navigateTo(day)
}

function moveDay(offset: number) {
    navigateTo(wrapDay(selectedDay.value.plus({ days: offset })))
}

function moveMonth(offset: number) {
    if (animating.value) return
    animating.value = false
    stripOffset.value = 0
    pendingDay = null
    let target = selectedDay.value.plus({ months: offset })
    // Keep in leap year bounds
    target = DateTime.utc(LEAP_YEAR, target.month, Math.min(target.day, DateTime.utc(LEAP_YEAR, target.month).daysInMonth!))
    selectedDay.value = target
    monthDirection.value = offset > 0 ? 'month-left' : 'month-right'
    displayMonthText.value = target.toFormat('MMMM')
}

// Sort by relationship proximity: direct hops + reverse hops * 2
function getProximityScore(item: TodayItem) {
    if (!item.relationPath) return Infinity
    return item.relationPath.startPersonPath.length + item.relationPath.toPersonPath.length * 2
}

const selectedDayItems = computed(() => {
    const key = dayKey(selectedDay.value)
    const items = allEventsMap.value.get(key) ?? []
    return [...items].sort((a, b) => getProximityScore(a) - getProximityScore(b))
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(CalendarIcon),
      _hoisted_3
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("button", {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (moveMonth(-1))),
          class: "nav-btn"
        }, [
          _createVNode(_unref(ChevronLeftIcon), { size: "16" })
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createVNode(_Transition, { name: monthDirection.value }, {
            default: _withCtx(() => [
              (_openBlock(), _createElementBlock("div", {
                class: "month-label",
                key: displayMonthText.value
              }, _toDisplayString(displayMonthText.value), 1))
            ]),
            _: 1
          }, 8, ["name"])
        ]),
        _createElementVNode("button", {
          onClick: _cache[1] || (_cache[1] = ($event: any) => (moveMonth(1))),
          class: "nav-btn"
        }, [
          _createVNode(_unref(ChevronRightIcon), { size: "16" })
        ])
      ]),
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("button", {
          onClick: _cache[2] || (_cache[2] = ($event: any) => (moveDay(-1))),
          class: "nav-btn"
        }, [
          _createVNode(_unref(ChevronLeftIcon), { size: "18" })
        ]),
        _createElementVNode("div", _hoisted_8, [
          _createElementVNode("div", {
            class: "day-strip",
            style: _normalizeStyle(stripStyle.value),
            onTransitionend: onSlideEnd
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(extendedDays.value, (day) => {
              return (_openBlock(), _createElementBlock("div", {
                key: dayKey(day),
                class: _normalizeClass(["day-box", { today: isToday(day) }]),
                onClick: ($event: any) => (selectDay(day))
              }, [
                _createElementVNode("span", {
                  class: _normalizeClass(["day-number", { 'no-events': !hasEvents(day) }])
                }, _toDisplayString(day.day), 3),
                _createElementVNode("span", {
                  class: _normalizeClass(["event-dot", { visible: hasEvents(day) }])
                }, null, 2),
                (isToday(day))
                  ? (_openBlock(), _createElementBlock("span", _hoisted_10, "TODAY"))
                  : _createCommentVNode("", true)
              ], 10, _hoisted_9))
            }), 128))
          ], 36),
          _hoisted_11
        ]),
        _createElementVNode("button", {
          onClick: _cache[3] || (_cache[3] = ($event: any) => (moveDay(1))),
          class: "nav-btn"
        }, [
          _createVNode(_unref(ChevronRightIcon), { size: "18" })
        ])
      ]),
      (scopeLoading.value)
        ? (_openBlock(), _createBlock(ScopeLoadProgress, { key: 0 }))
        : (_openBlock(), _createBlock(TodayList, {
            key: 1,
            items: selectedDayItems.value
          }, null, 8, ["items"]))
    ])
  ]))
}
}

})